/**
 * SpriteRenderer.ts — Sprite sheet descriptors and animation clip mapping.
 * Platform-agnostic data model; draw calls in GameCanvas.web.tsx / GameCanvas.tsx.
 *
 * Sheet format: strips or grids, no padding, uniform frame size.
 *   srcX = (idx % cols) * frameWidth, srcY = floor(idx / cols) * frameHeight
 *
 * @module SpriteRenderer
 */

import type { NovaAnimState } from '../engine/gameState';

// Re-export draw offsets from their dedicated module
export type { DrawOffset } from './drawOffsets';
export {
  NOVA_DRAW_OFFSET,
  NIBBLER_DRAW_OFFSET,
  THORNHOP_DRAW_OFFSET,
  GLOOM_BAT_DRAW_OFFSET,
} from './drawOffsets';

// ── Sprite sheet descriptor ────────────────────────────────────────────────

/** Describes the layout of a sprite sheet atlas. */
export interface SpriteSheet {
  sheetWidth: number;
  sheetHeight: number;
  frameWidth: number;
  frameHeight: number;
  frameCount: number;
}

// ── Animation clip ────────────────────────────────────────────────────────

/** A named range of frames within a sprite sheet. */
export interface AnimClip {
  start: number;
  end: number;
  fps: number;
  loop: boolean;
}

// ── Nova sprite sheet ─────────────────────────────────────────────────────

// DECISION: nova-v2-sheet.png is a single-row strip of 60 pixel-art frames
// at 60×60 px each (3600 × 60 total). Switched from the v4 221×221 grid
// for a crisper pixel-art style that matches the 16×16 tile aesthetic.

/**
 * Nova sprite sheet (v2): 3600 × 60 px, 60 frames in a single row.
 *
 * Frame map (0-indexed, left→right):
 *   0-3:   Idle (breathing sway)
 *   4-11:  Run cycle
 *   12-13: Run stop / skid
 *   14:    Jump crouch (squat prep)
 *   15-16: Jump rise
 *   17-19: Aerial flip / somersault
 *   20:    Fall / vertical drop
 *   21:    Land dust burst
 *   22:    Aerial spread (fall alt)
 *   23-28: Standing / walk variants
 *   29:    Aim right
 *   30:    Fire right (muzzle flash)
 *   31-33: Post-fire / attack motion
 *   34-37: Death explosion effect (progressive)
 *   38-41: Recovery / walk
 *   42:    Aim / fire up
 *   43-44: Action poses
 *   45-48: Wall slide
 *   49:    Standing neutral
 *   50:    Hurt / knockback
 *   51-56: Walk / decel variants
 *   57-59: Standing / special
 */
export const NOVA_SHEET: SpriteSheet = {
  sheetWidth: 3600,
  sheetHeight: 60,
  frameWidth: 60,
  frameHeight: 60,
  frameCount: 60,
};

/** Nova animation clips — one entry per NovaAnimState. */
export const NOVA_CLIPS: Record<NovaAnimState, AnimClip> = {
  // ── Movement ──
  IDLE:       { start:  0, end:  3, fps: 3,  loop: true  },
  RUN_START:  { start:  4, end:  5, fps: 15, loop: false },
  RUN:        { start:  4, end: 11, fps: 12, loop: true  },
  RUN_STOP:   { start: 12, end: 13, fps: 12, loop: false },
  CROUCH:     { start: 14, end: 14, fps: 1,  loop: true  },
  JUMP_CROUCH:{ start: 14, end: 14, fps: 15, loop: false },
  JUMP:       { start: 15, end: 16, fps: 10, loop: false },
  FALL:       { start: 20, end: 20, fps: 8,  loop: true  },
  LAND:       { start: 20, end: 21, fps: 12, loop: false },
  // ── Combat ──
  STOMP:      { start: 17, end: 19, fps: 14, loop: false },
  HURT:       { start: 50, end: 50, fps: 12, loop: false },
  DEAD:       { start: 35, end: 37, fps: 4,  loop: false },
  // ── Aim / Fire (future — need aim input system, see ROADMAP) ──
  // DECISION: v2 sheet lacks 8-directional aim frames; reuse AIM_RIGHT (29)
  // for horizontal directions and AIM_UP (42) for upward directions.
  AIM_RIGHT:      { start: 29, end: 29, fps: 1, loop: true },
  AIM_UP_RIGHT:   { start: 29, end: 29, fps: 1, loop: true },
  AIM_UP:         { start: 42, end: 42, fps: 1, loop: true },
  AIM_UP_LEFT:    { start: 42, end: 42, fps: 1, loop: true },
  AIM_LEFT:       { start: 29, end: 29, fps: 1, loop: true },
  AIM_DOWN_LEFT:  { start: 29, end: 29, fps: 1, loop: true },
  AIM_DOWN:       { start: 29, end: 29, fps: 1, loop: true },
  AIM_DOWN_RIGHT: { start: 29, end: 29, fps: 1, loop: true },
  JUMP_FIRE_RIGHT:    { start: 30, end: 30, fps: 1, loop: true },
  JUMP_FIRE_UP_RIGHT: { start: 42, end: 42, fps: 1, loop: true },
};

// Re-export enemy sprite data so existing importers keep working.
export {
  NIBBLER_SHEET, NIBBLER_CLIPS,
  THORNHOP_SHEET, THORNHOP_CLIPS,
  GLOOM_BAT_SHEET, GLOOM_BAT_CLIPS,
} from './enemySpriteData';
export type {
  NibblerAnimState, ThornhopAnimState, GloomBatAnimState,
} from './enemySpriteData';

// ── Laser hit effect ──────────────────────────────────────────────────────

/**
 * Laser hit effect sheet: 2172 × 418 px, 6 frames at 362 × 418 px.
 * Vertically cropped from 724 px (content rows 139–556).
 * Frame map (0-indexed):
 *   0: Contact Spark, 1: Small Burst, 2: Medium Burst,
 *   3: Peak Burst, 4: Fragment Breakup, 5: Fade Out
 */
export const LASER_HIT_SHEET: SpriteSheet = {
  sheetWidth: 2172,
  sheetHeight: 418,
  frameWidth: 362,
  frameHeight: 418,
  frameCount: 6,
};

export type LaserHitAnimState = 'BURST';

/** Single one-shot burst clip across all 6 frames at ~16 fps (~375ms total). */
export const LASER_HIT_CLIPS: Record<LaserHitAnimState, AnimClip> = {
  // DECISION: Variable timing per the user's script (30-90ms per frame) averages
  // ~55ms/frame ≈ 18 fps, but 16 fps gives a slightly more visible burst.
  BURST: { start: 0, end: 5, fps: 16, loop: false },
};

// ── Nova ship + flame sprite sheets ───────────────────────────────────────

/** Nova ship sprite: 128 × 48 px, single static frame. */
export const NOVA_SHIP_SHEET: SpriteSheet = {
  sheetWidth: 128,
  sheetHeight: 48,
  frameWidth: 128,
  frameHeight: 48,
  frameCount: 1,
};

/** Flame attachment point in ship-local coordinates (engine nozzle). */
export const FLAME_ANCHOR = { x: 16, y: 24 } as const;

/** Flame idle sheet: 48 × 14 px, 4 frames at 12 × 14 px. */
export const FLAME_IDLE_SHEET: SpriteSheet = {
  sheetWidth: 48, sheetHeight: 14, frameWidth: 12, frameHeight: 14, frameCount: 4,
};
/** Flame cruise sheet: 80 × 14 px, 4 frames at 20 × 14 px. */
export const FLAME_CRUISE_SHEET: SpriteSheet = {
  sheetWidth: 80, sheetHeight: 14, frameWidth: 20, frameHeight: 14, frameCount: 4,
};
/** Flame boost sheet: 128 × 14 px, 4 frames at 32 × 14 px. */
export const FLAME_BOOST_SHEET: SpriteSheet = {
  sheetWidth: 128, sheetHeight: 14, frameWidth: 32, frameHeight: 14, frameCount: 4,
};

/** Looping 4-frame flame animation clip at 10 fps. */
export const FLAME_CLIP: AnimClip = { start: 0, end: 3, fps: 10, loop: true };

/** Which flame variant to render behind the ship. */
export type FlameMode = 'NONE' | 'IDLE' | 'CRUISE' | 'BOOST';

// ── Animation timer ───────────────────────────────────────────────────────

/**
 * Advance an animation timer and return the current frame index.
 *
 * @param elapsed   - Seconds elapsed since the clip started (mutable, caller owns).
 * @param clip      - The animation clip to play.
 * @param dt        - Delta time this tick.
 * @returns The absolute frame index into the sprite sheet.
 */
export function advanceAnim(
  elapsed: number,
  clip: AnimClip,
  dt: number,
): { frameIndex: number; elapsed: number } {
  const newElapsed = elapsed + dt;
  const clipLen = clip.end - clip.start + 1;
  // DECISION: count frames in integer steps (floor(elapsed * fps)) instead of
  // computing modulo over a floating-point duration. Modulo of a sum of frame
  // periods accumulates floating-point error that drops a frame at clip
  // boundaries (e.g. 0.5 % 0.4 ≈ 0.0999... → frame 0 instead of 1).
  const totalFrames = Math.floor(newElapsed * clip.fps);
  let localFrame: number;
  if (clip.loop) {
    // Modulo on an integer is exact, so the loop boundary lands on the right frame.
    localFrame = ((totalFrames % clipLen) + clipLen) % clipLen;
  } else {
    localFrame = Math.min(totalFrames, clipLen - 1);
  }
  return {
    frameIndex: clip.start + localFrame,
    elapsed: newElapsed,
  };
}

/**
 * Compute the source X offset for a frame within a sheet.
 * Supports both single-row strips and multi-row grids.
 *
 * @param sheet      - The sprite sheet descriptor.
 * @param frameIndex - Zero-based frame index.
 * @returns Pixel X offset into the sheet.
 */
export function frameSourceX(sheet: SpriteSheet, frameIndex: number): number {
  // DECISION: Grid-aware calculation — works for single-row (cols = frameCount)
  // and multi-row layouts. For single-row sheets idx % cols === idx.
  const cols = Math.floor(sheet.sheetWidth / sheet.frameWidth);
  const idx = Math.min(frameIndex, sheet.frameCount - 1);
  return (idx % cols) * sheet.frameWidth;
}

/**
 * Compute the source Y offset for a frame within a sheet.
 * Returns 0 for single-row strips, row * frameHeight for grids.
 *
 * @param sheet      - The sprite sheet descriptor.
 * @param frameIndex - Zero-based frame index.
 * @returns Pixel Y offset into the sheet.
 */
export function frameSourceY(sheet: SpriteSheet, frameIndex: number): number {
  const cols = Math.floor(sheet.sheetWidth / sheet.frameWidth);
  const idx = Math.min(frameIndex, sheet.frameCount - 1);
  return Math.floor(idx / cols) * sheet.frameHeight;
}
